/**
 * Phase 5.5-Lite — Super-admin payment settings card.
 *
 * Lives at the top of /dispatch/settings (super-admin only). Manages:
 *   • Cash App handle (cashtag)
 *   • Display name shown to customers
 *   • Toggle: show payment routing to drivers on their dashboards
 *
 * QR is auto-generated server-side from the handle. Live preview shown.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { CreditCard, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export function PaymentSettingsCard() {
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [handle, setHandle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showRouting, setShowRouting] = useState(true);
  const [previewQR, setPreviewQR] = useState(null);
  const [previewLink, setPreviewLink] = useState(null);

  const refreshPreview = useCallback(async () => {
    if (!handle || !handle.trim()) {
      setPreviewQR(null);
      setPreviewLink(null);
      return;
    }
    try {
      // Use a sample $25 / DRAFT order so the operator can visualize what customers see
      const { data } = await axios.post(
        `${API_URL}/api/payment-render/preview`,
        { amount: 25.0, draft_order_short: 'PREVIEW' },
        { withCredentials: true },
      );
      if (data.configured) {
        setPreviewQR(data.qr_data_url);
        setPreviewLink(data.deep_link);
      } else {
        setPreviewQR(null);
        setPreviewLink(null);
      }
    } catch (_) {
      setPreviewQR(null);
    }
  }, [handle]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/payment-settings`, { withCredentials: true });
        setHandle(data.cashapp_handle || '');
        setDisplayName(data.display_name || 'Ash & Merch');
        setShowRouting(data.show_routing_to_drivers !== false);
      } catch (err) {
        toast.error('Could not load payment settings');
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  // Refresh preview after save (or when explicit refresh button pressed)
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        cashapp_handle: handle.trim(),
        display_name: displayName.trim() || 'Ash & Merch',
        show_routing_to_drivers: showRouting,
      };
      await axios.put(`${API_URL}/api/payment-settings`, payload, { withCredentials: true });
      toast.success('Payment settings saved');
      await refreshPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="bg-[#0C0612] border border-[#FFD60A] p-5 mb-6 space-y-4" data-testid="payment-settings-card">
      <div className="flex items-center justify-between">
        <h3 className="text-[#FFD60A] font-mono text-base uppercase tracking-wider flex items-center gap-2">
          <CreditCard className="w-4 h-4" />
          // PAYMENT METHODS
        </h3>
        <span className="text-[#6B8E73] font-mono text-xs uppercase">SUPER-ADMIN ONLY</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div>
            <label className="block text-[#A3C7A8] font-mono text-xs uppercase mb-1">Cash App handle (cashtag)</label>
            <Input
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="$YourHandle"
              className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
              data-testid="cashapp-handle-input"
            />
            <p className="text-[#4B6B52] font-mono text-[10px] mt-1">
              // Format: $Letters_Digits_Hyphens (max 20 chars). Leading $ optional.
            </p>
          </div>

          <div>
            <label className="block text-[#A3C7A8] font-mono text-xs uppercase mb-1">
              Display name (what customers see)
            </label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Ash & Merch"
              className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
              data-testid="cashapp-display-name-input"
            />
          </div>

          <div className="flex items-center justify-between bg-[#050003] border border-[#2A0F3D] p-3">
            <div>
              <p className="text-[#A3C7A8] font-mono text-xs uppercase">Show routing to drivers</p>
              <p className="text-[#4B6B52] font-mono text-[10px] mt-0.5">
                // When ON, drivers see "Cash App routes to $Handle" on their dashboard.
              </p>
            </div>
            <Switch
              checked={showRouting}
              onCheckedChange={setShowRouting}
              data-testid="cashapp-show-routing-toggle"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              onClick={save}
              disabled={saving}
              className="flex-1 bg-[#FFD60A] hover:bg-[#FFC107] text-[#050003] font-mono uppercase tracking-wider rounded-none border-0"
              data-testid="cashapp-save-btn"
            >
              {saving ? '// SAVING…' : '> SAVE'}
            </Button>
            <Button
              onClick={refreshPreview}
              variant="outline"
              className="border-[#2A0F3D] text-[#A3C7A8] hover:bg-[#2A0F3D] hover:text-[#FFD60A] font-mono rounded-none"
              data-testid="cashapp-refresh-preview-btn"
              title="Regenerate preview from current handle"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-[#A3C7A8] font-mono text-xs uppercase">// PREVIEW (sample $25 order)</label>
          <div className="bg-[#050003] border border-[#2A0F3D] p-4 flex flex-col items-center min-h-[200px] justify-center">
            {previewQR ? (
              <>
                <img
                  src={previewQR}
                  alt="Cash App QR preview"
                  className="w-40 h-40 border border-[#2A0F3D] bg-white p-2"
                  data-testid="cashapp-qr-preview"
                />
                <p className="text-[#A3C7A8] font-mono text-xs uppercase mt-2">{displayName || 'Ash & Merch'}</p>
                <a
                  href={previewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#39FF14] font-mono text-[10px] mt-1 hover:underline break-all text-center"
                  data-testid="cashapp-deeplink-preview"
                >
                  {previewLink}
                </a>
              </>
            ) : (
              <p className="text-[#6B8E73] font-mono text-xs text-center">
                // Save a valid handle to see the QR + deep link preview
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="pt-2 border-t border-[#2A0F3D]">
        <p className="text-[#6B8E73] font-mono text-[10px] uppercase">
          // Per-driver overrides:&nbsp;
          <a href="/dispatch/admin/users" className="text-[#FFD60A] hover:underline" data-testid="link-driver-overrides">
            manage on /dispatch/admin/users → click driver → cash app override
          </a>
        </p>
      </div>
    </div>
  );
}
